/**
 * Bundle builder for the "infinite" Simple Bundles templates.
 *
 * Adding to cart is deliberately NOT handled here. The inputs carry a form
 * attribute pointing at the product form, so the theme's own product-form element
 * submits them and owns the drawer, section bundling and error handling. Required
 * inputs block an incomplete bundle through native validation.
 *
 * What is left for this script: keeping _bundle_selection in sync, blocking a
 * sold-out slot, and editing an existing bundle from the cart, which has to go to
 * cart/change.js rather than cart/add.js so it updates the line instead of adding
 * a second one. The line to rewrite arrives as ?edit_bundle=<line key>, set by the
 * edit link on the cart line.
 */
const BUNDLE_SELECTION_SEPARATOR = ' <> ';
const EDIT_KEY_PARAM = 'edit_bundle';

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    this.selectionInput = this.querySelector('[data-bundle-selection]');
    this.groups = Array.from(this.querySelectorAll('[data-bundle-group]'));

    this.onChange = () => this.sync();
    this.onSubmit = (event) => this.handleSubmit(event);

    this.addEventListener('change', this.onChange);

    this.pendingImages = new Set();
    this.editKey = new URLSearchParams(location.search).get(EDIT_KEY_PARAM) || '';

    const firstButton = this.submitButtons()[0];
    const firstContent = firstButton?.querySelector('button-content') || firstButton;
    this.defaultSubmitLabel = firstContent?.textContent.trim() || '';

    this.sync();

    if (this.editKey) {
      // Capture on an ancestor, so this runs before the form's own submit handler.
      document.documentElement.addEventListener('submit', this.onSubmit, { capture: true });
      this.restoreSelection();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    document.documentElement.removeEventListener('submit', this.onSubmit, { capture: true });
  }

  chosenElement(group) {
    return (
      group.querySelector('[data-bundle-select]') ||
      group.querySelector('input[data-bundle-input]:checked') ||
      group.querySelector('input[type="hidden"][data-bundle-input]')
    );
  }

  valueOf(group) {
    const element = this.chosenElement(group);
    return element ? element.value : '';
  }

  inventoryOf(group) {
    return parseInt(this.chosenSource(group)?.dataset.inventory ?? '0', 10);
  }

  get properties() {
    const properties = {};
    const values = [];

    this.groups.forEach((group) => {
      const value = this.valueOf(group);
      if (!value) return;
      properties[group.dataset.groupName] = value;
      values.push(value);
    });

    if (values.length === this.groups.length) {
      properties._bundle_selection = values.join(BUNDLE_SELECTION_SEPARATOR);
    }

    return properties;
  }

  /** The option or radio standing for the current choice, which carries its data. */
  chosenSource(group) {
    const element = this.chosenElement(group);
    if (!element) return null;
    return element.tagName === 'SELECT' ? element.selectedOptions[0] : element;
  }

  // Reflects the chosen component into the slot's thumbnail and caption. The data
  // is already on each option, so there is nothing to fetch.
  showChoice(group) {
    const source = this.chosenSource(group);
    const media = group.querySelector('[data-bundle-media]');
    const image = group.querySelector('[data-bundle-media-image]');
    const caption = group.querySelector('[data-bundle-chosen]');

    const src = source?.dataset.image || '';

    if (media && image) {
      media.hidden = src === '';
      if (src && image.getAttribute('src') !== src) image.src = src;

      const url = source?.dataset.url;
      if (url) media.href = url;
      else media.removeAttribute('href');
    }

    if (caption) caption.textContent = source?.dataset.caption || '';

    if (!src && source?.dataset.handle) this.fetchImage(source, group);
  }

  /**
   * Fills in a component Liquid could not resolve. all_products stops at 20 products
   * per page, so a bundle with more components than that has an image missing here.
   * The result is written back onto the option, so each one is fetched once.
   */
  async fetchImage(source, group) {
    const handle = source.dataset.handle;
    if (this.pendingImages.has(handle)) return;
    this.pendingImages.add(handle);

    try {
      const response = await fetch(`${Shopify.routes.root}products/${handle}.js`);
      if (!response.ok) return;

      const product = await response.json();
      if (!product.featured_image) return;

      source.dataset.image = product.featured_image;
      source.dataset.url = `${Shopify.routes.root}products/${handle}`;
    } catch (error) {
      return;
    } finally {
      this.pendingImages.delete(handle);
    }

    // Only paint if this is still the choice; the customer may have moved on.
    if (this.chosenSource(group) === source) this.showChoice(group);
  }

  sync() {
    this.groups.forEach((group) => this.showChoice(group));

    const values = this.groups.map((group) => this.valueOf(group));
    const complete = values.every(Boolean);

    if (this.selectionInput) {
      this.selectionInput.value = complete ? values.join(BUNDLE_SELECTION_SEPARATOR) : '';
    }

    // A sold-out value is disabled and so cannot be picked; this catches the
    // single-value slots, which are posted whether they are in stock or not.
    const soldOut = this.groups.some((group) => this.valueOf(group) && this.inventoryOf(group) < 1);
    this.toggleAttribute('data-bundle-sold-out', soldOut);

    this.submitButtons().forEach((button) => {
      button.disabled = soldOut;
    });
  }

  submitButtons() {
    if (!this.form) return [];
    return Array.from(this.form.elements).filter((element) => element.type === 'submit');
  }

  relabelSubmit(text) {
    this.submitButtons().forEach((button) => {
      const content = button.querySelector('button-content') || button;
      content.textContent = text;

      // The button animations duplicate the label through these attributes.
      ['rotated-text', 'up-text'].forEach((name) => {
        if (content.hasAttribute(name)) content.setAttribute(name, text);
      });
    });
  }

  async restoreSelection() {
    let cart;
    try {
      cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    } catch (error) {
      return;
    }

    const line = cart.items?.find((item) => item.key === this.editKey);
    if (!line) {
      // The line has gone, so this is an ordinary add again.
      this.editKey = '';
      document.documentElement.removeEventListener('submit', this.onSubmit, { capture: true });
      return;
    }

    const saved = (line.properties?._bundle_selection || '').split(BUNDLE_SELECTION_SEPARATOR);

    this.groups.forEach((group, index) => {
      const value = saved[index];
      if (!value) return;

      const select = group.querySelector('[data-bundle-select]');
      if (select) {
        select.value = value;
        return;
      }

      const radio = group.querySelector(`input[data-bundle-input][value="${CSS.escape(value)}"]`);
      if (radio) radio.checked = true;
    });

    this.setAttribute('data-bundle-editing', '');
    this.relabelSubmit('Update bundle');
    this.sync();
  }

  async handleSubmit(event) {
    if (!this.editKey || event.target !== this.form) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const buttons = this.submitButtons();
    buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));

    // Same section bundling the theme uses, so the drawer and cart re-render.
    const sections = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    try {
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: this.editKey,
          quantity: 1,
          properties: this.properties,
          sections: [...sections].join(',')
        })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        this.form.dispatchEvent(
          new CustomEvent('cart:error', { bubbles: true, detail: { error: responseJson.message } })
        );
        return;
      }

      if (window.themeVariables?.settings?.cartType === 'page') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cart.sections = responseJson.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'cart:update', cart } })
      );

      // Done editing: drop the parameter so a reload adds rather than rewrites.
      this.editKey = '';
      this.removeAttribute('data-bundle-editing');
      this.relabelSubmit(this.defaultSubmitLabel);
      document.documentElement.removeEventListener('submit', this.onSubmit, { capture: true });

      const url = new URL(location.href);
      url.searchParams.delete(EDIT_KEY_PARAM);
      history.replaceState({}, '', url);
    } finally {
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
